import { FC, useRef, useState } from 'react';
import { Reactions } from './Reactions';
import { Text } from '../../components/Text';
import { ICaremapNote } from '../model/NotesClient';
import { createUseStyles } from 'react-jss';
import { INoteInteractionInfoType, NoteInteractionModal } from '../view-note/NoteInteractionModal';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { CommentIcon, PaperPlaneIcon } from '../../theme/Icons';
import { useLongPress } from '@uidotdev/usehooks';
import { isPlatform } from '../../utils/isPlatform';
import { CommentsModelFactory } from '../../comments/CommentsModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { AddCommentButton } from '../../comments/AddCommentButton';
import { useBreakpoints } from '../../theme/useBreakpoints';
import { INoteInteractionSurface } from './INoteInteractionSurface';

const useStyles = createUseStyles({
    interactionsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between'
    },

    supporterInfoRow: {
        gap: 16,
        flexGrow: 1,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    smallText: {
        cursor: 'pointer',
        marginTop:4,
        fontSize: 14
    },

    reactionsRow: {
        marginBottom: 8
    },

    cntIndicator: {
        display: 'flex',
        alignItems: 'center'
    }
});

type ICommentCountProps = {
    note: ICaremapNote;
    onClick: (note: ICaremapNote) => void;
};

const CommentCount: FC<ICommentCountProps & { count: number; }> = ({ note, onClick, count }) => {
    const classes = useStyles();
    const { isAboveBreakpoint } = useBreakpoints();

    if (!count) {
        return null;
    }

    return (
        <div className={classes.smallText} onClick={() => onClick(note)}>
            <Text type="detail" data-type="post-comment-count">
                <div className={classes.cntIndicator}>
                    <CommentIcon color="medium" />&nbsp;{count}{isAboveBreakpoint('sm') ? ` Comment${count > 1 ? 's' : ''}` : ''}
                </div>
            </Text>
        </div>
    );
};

// Counts the note's loaded comments, loading them if they are not yet, and follows their changes.
const LoadedCommentCount: FC<ICommentCountProps> = ({ note, onClick }) => {
    const factory = useInstance(CommentsModelFactory);
    useSubscription(() => factory);

    return <CommentCount note={note} onClick={onClick} count={factory.forNote(note).getComments().length} />;
};

export type IReactionsRowProps = {
    note: ICaremapNote;
    surface: INoteInteractionSurface;
    authenticatedRedirect: string;
    // shows the add-comment button, for a host that can take the reader to the comment editor
    addComment?: (note: ICaremapNote) => void;
    commentCntClickHandler?: (note: ICaremapNote) => void;
    // the note's own commentCount, shown instead of loading its comments, for a row that shows many
    // notes (the notes list); a note view loads its comments anyway, so it counts those
    commentCount?: number;
};

export const ReactionsRow: FC<IReactionsRowProps> = ({ note, surface, authenticatedRedirect, addComment, commentCntClickHandler, commentCount }) => {
    const classes = useStyles();

    const reactionsModel = useInstance(CaremapNotesModelFactory).forActiveCaremap().reactions;

    const [modalInfo, setModalInfo] = useState<{ note: ICaremapNote, type: INoteInteractionInfoType } | undefined>();

    // using state didn't work, my guess is due to how the long press hook is set up, so using a ref works
    const modalType = useRef<{ type: INoteInteractionInfoType }>({type: 'reactions'});

    const { isAboveBreakpoint } = useBreakpoints();
    const isAboveSm = isAboveBreakpoint('sm');

    const showInfoModal = (type: INoteInteractionInfoType) => {
        setModalInfo({ type: modalType.current.type, note });
    };

    const isDesktop = isPlatform('desktop');
    const onLongPress = (e: Event) => {
        // on desktop, it's just a 'click' for Sent to show the modal and no events to show the modal on reactions
        if (!isDesktop) {
            showInfoModal(modalType.current?.type);
        }
    };

    const longPressAttrs = useLongPress(e => onLongPress(e), {
        threshold: 500,
        onStart: (e: any) => {
            if (!isDesktop) {
                modalType.current.type = 'reactions';
            }
        }
    });

    const showNotified = () => {
        modalType.current.type = 'notifications';
        showInfoModal(modalType.current?.type);
    };

    const getNotificationCount = () => {
        const cnt = reactionsModel.getNames({ note, reaction: 'notification-sent:email' }).length;
        return cnt ?
            <div className={classes.cntIndicator}>
                <PaperPlaneIcon color="medium" />&nbsp;{cnt}{isAboveSm && ' Notified'}
            </div> : '';
    };

    const notificationCnt = getNotificationCount();

    return (
        <>
            <div
                {...longPressAttrs}
                className={classes.interactionsRow}
                data-type="reactions-row"
            >
                <div className={classes.reactionsRow}>
                    <Reactions note={note} surface={surface} authenticatedRedirect={authenticatedRedirect} />
                </div>
                <div className={classes.supporterInfoRow}>
                    <div style={{flex: 1}}>&nbsp;</div>
                    {addComment && (
                        <AddCommentButton
                            onClick={() => addComment(note)}
                            authenticatedRedirect={authenticatedRedirect}
                        />
                    )}
                    {commentCntClickHandler && (commentCount !== undefined
                        ? <CommentCount note={note} onClick={commentCntClickHandler} count={commentCount} />
                        : <LoadedCommentCount note={note} onClick={commentCntClickHandler} />
                    )}
                    {notificationCnt && (
                        <div className={classes.smallText} onClick={showNotified}>
                            <Text type="detail" data-type="post-notification-count">
                                {notificationCnt}
                            </Text>
                        </div>
                    )}
                </div>
            </div>

            <NoteInteractionModal
                modalInfo={modalInfo}
                surface={surface}
                closeHandler={() => setModalInfo(undefined)}
            />
        </>
    );
};
